import { DatePipe } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';

@Component({
  selector: 'app-date-time-picker',
  templateUrl: './date-time-picker.component.html',
  styleUrl: './date-time-picker.component.css'
})
export class DateTimePickerComponent {

  @Input() initialDate: Date | null = new Date();
  @Input() label: string = 'Select Date & Time';
  @Input() appearance: 'outline' | 'fill' = 'outline';

  @Output() dateChange = new EventEmitter<Date>();

  @ViewChild('hourWheel') hourWheel!: ElementRef;
  @ViewChild('minWheel') minWheel!: ElementRef;
  @ViewChild('secWheel') secWheel!: ElementRef;

  toDateFromPicker: Date | null = new Date();
  displayLabel: string = '';

  hours = Array.from({ length: 24 }, (_, i) => i);
  minutes = Array.from({ length: 60 }, (_, i) => i);
  seconds = Array.from({ length: 60 }, (_, i) => i);

  dateControl = new FormControl(new Date());
  hourControl = new FormControl(0);
  minuteControl = new FormControl(0);
  secondControl = new FormControl(0);

  constructor(private _datePipe: DatePipe) { }

  ngOnInit(): void {
    if (this.initialDate) {
      this.toDateFromPicker = new Date(this.initialDate);
      this.syncWheelsToConfirmedValue();
      this._updateDisplayLabel(this.toDateFromPicker);
    }
  }

  onManualType(type: 'hour' | 'minute' | 'second') {
    const control: any = this._getControl(type);
    let value = parseInt(control.value, 10);
    const max = type === 'hour' ? 23 : 59;
    if (isNaN(value) || value < 0) value = 0;
    if (value > max) value = max;
    control.setValue(value);
  }

  forwardScroll(event: WheelEvent, type: 'hour' | 'minute' | 'second') {
    event.preventDefault();
    const wheelEl = this._getWheelEl(type);
    if (wheelEl) {
      wheelEl.nativeElement.scrollTop += event.deltaY;
    }
  }

  onScroll(event: any, type: 'hour' | 'minute' | 'second') {
    const scrollTop = event.target.scrollTop;
    const selectedValue = Math.round(scrollTop / 30);
    const list = this._getList(type);
    const val = list[selectedValue] !== undefined ? list[selectedValue] : 0;

    if (type === 'hour') this.hourControl.setValue(val);
    if (type === 'minute') this.minuteControl.setValue(val);
    if (type === 'second') this.secondControl.setValue(val);
  }

  step(type: 'hour' | 'minute' | 'second', direction: number) {
    const control = this._getControl(type);
    const wheelEl = this._getWheelEl(type);
    const list = this._getList(type);
    let nextValue = (control?.value || 0) + direction;

    if (nextValue < 0) nextValue = list.length - 1;
    if (nextValue >= list.length) nextValue = 0;

    control?.setValue(nextValue);
    wheelEl?.nativeElement?.scrollTo({
      top: nextValue * 30,
      behavior: 'smooth'
    });
  }

  syncWheelsToConfirmedValue() {
    const referenceDate = this.toDateFromPicker || new Date();
    this.dateControl?.setValue(referenceDate);
    this.hourControl?.setValue(referenceDate.getHours());
    this.minuteControl?.setValue(referenceDate.getMinutes());
    this.secondControl?.setValue(referenceDate.getSeconds());

    setTimeout(() => {
      if (this.hourWheel) this.hourWheel.nativeElement.scrollTop = this.hourControl?.value * 30;
      if (this.minWheel) this.minWheel.nativeElement.scrollTop = this.minuteControl?.value * 30;
      if (this.secWheel) this.secWheel.nativeElement.scrollTop = this.secondControl?.value * 30;
    }, 100);
  }

  applyTime() {
    const date = new Date(this.dateControl?.value!);
    date.setHours(this.hourControl?.value!, this.minuteControl?.value!, this.secondControl?.value!);
    this.toDateFromPicker = date;
    this._updateDisplayLabel(date);
    this.dateChange.emit(date);
  }

  private _updateDisplayLabel(date: Date) {
    this.displayLabel = this._datePipe?.transform(date, 'd MMM yyyy, hh:mm:ss a') || '';
  }

  private _getControl(type: string) {
    if (type === 'hour') return this.hourControl;
    if (type === 'minute') return this.minuteControl;
    return this.secondControl;
  }

  private _getWheelEl(type: string) {
    if (type === 'hour') return this.hourWheel;
    if (type === 'minute') return this.minWheel;
    return this.secWheel;
  }

  private _getList(type: string): number[] {
    if (type === 'hour') return this.hours;
    if (type === 'minute') return this.minutes;
    return this.seconds;
  }
}
